// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the live state of the pull requests agent threads link to, read from GitHub by the API
// with the person's own GitHub sign-in (POST /konvo/agents/pulls/lookup). The thread's PR card and
// the list rows read it from here; the bridge only says which pull requests a thread has.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import {http} from '@app/features/platform/transport/RestTransport';
import {
	AGENT_PULL_LOOKUP_MAX,
	type AgentMergeAsk,
	type AgentMergeRights,
	type AgentOpenPulls,
	type AgentPullAction,
	type AgentPullActionResult,
	type AgentPullAsked,
	type AgentPullCheck,
	type AgentPullConversation,
	type AgentPullDetails,
	type AgentPullLookup,
	type AgentPullMergeMethod,
	type AgentPullMergeResult,
	type AgentPullRef,
	type AgentPullRequest,
	type AgentPullReview,
	type AgentPullStatus,
	type AgentThreadSummary,
} from '@konvo/schema/src/domains/konvo/Agents';
import {makeAutoObservable, observable, runInAction} from 'mobx';

export const pullKey = (ref: AgentPullRef) => `${ref.repository.toLowerCase()}#${ref.number}`;

const linkedMs = (pull: AgentPullRequest) => {
	const ms = Date.parse(pull.linkedAt ?? '');
	return Number.isFinite(ms) ? ms : Number.NEGATIVE_INFINITY;
};

/**
 * The pull request a thread's card follows. When the bridge says when each was linked
 * (konvo-agents 0.62.0+): the newest link that isn't closed, so a chat that opens one after
 * another (the release train's Orchestrator) always shows its latest (JP 2026-11-02). Before
 * that: the one the agent tool links to the thread, else the newest open one, else the newest
 * merged one, else the newest. A closed one (say, folded into another PR) only wins when the
 * thread has nothing else. Uses GitHub's live state once read, as the bridge's can be stale. Null
 * when the thread has none.
 */
export function threadPull(
	thread: Pick<AgentThreadSummary, 'pullRequests'> | null | undefined,
): AgentPullRequest | null {
	const pulls = thread?.pullRequests ?? [];
	if (!pulls.length) return null;
	const stateOf = (pull: AgentPullRequest) => store.get(pull)?.state ?? pull.state;
	if (pulls.some((pull) => pull.linkedAt)) {
		const latest = [...pulls].sort((a, b) => {
			const at = linkedMs(b) - linkedMs(a);
			return Number.isNaN(at) || at === 0 ? b.number - a.number : at;
		});
		return latest.find((pull) => stateOf(pull) !== 'closed') ?? latest[0]!;
	}
	const newest = [...pulls].sort((a, b) => b.number - a.number);
	return (
		pulls.find((pull) => pull.linked && stateOf(pull) !== 'closed') ??
		newest.find((pull) => stateOf(pull) !== 'closed' && stateOf(pull) !== 'merged') ??
		newest.find((pull) => stateOf(pull) === 'merged') ??
		pulls.find((pull) => pull.linked) ??
		newest[0]!
	);
}

export interface ChecksTally {
	total: number;
	passed: number;
	failed: number;
	running: number;
	waiting: number;
	skipped: number;
	cancelled: number;
}

export function checksTally(checks: ReadonlyArray<AgentPullCheck>): ChecksTally {
	const tally: ChecksTally = {
		total: checks.length,
		passed: 0,
		failed: 0,
		running: 0,
		waiting: 0,
		skipped: 0,
		cancelled: 0,
	};
	for (const check of checks) tally[check.state]++;
	return tally;
}

/**
 * Where a pull request stands, for the card's tone and the list row. 'cancelled': nothing failed or
 * runs, but a check was stopped before it finished, so the checks have not passed either.
 */
export type PullPhase = 'merged' | 'closed' | 'failed' | 'running' | 'cancelled' | 'passed' | 'no_checks';

export function pullPhase(pull: AgentPullStatus): PullPhase {
	if (pull.state === 'merged') return 'merged';
	if (pull.state === 'closed') return 'closed';
	const tally = checksTally(pull.checks);
	if (tally.failed) return 'failed';
	if (tally.running || tally.waiting) return 'running';
	if (tally.cancelled) return 'cancelled';
	return tally.passed ? 'passed' : 'no_checks';
}

/** Whether to read it again soon: checks still running, or GitHub still working out mergeability. */
export function pullIsMoving(pull: AgentPullStatus): boolean {
	return pull.state === 'open' && (pullPhase(pull) === 'running' || pull.mergeable === null);
}

/** The prompt "Send to the agent" puts in the composer for failed checks. */
export function failedChecksPrompt(pull: AgentPullStatus): string {
	const failed = pull.checks.filter((check) => check.state === 'failed');
	const lines = failed.map(
		(check) => `- ${check.name}${check.summary ? `: ${check.summary}` : ''}${check.url ? ` (${check.url})` : ''}`,
	);
	return [`These checks failed on #${pull.number} (${pull.url}):`, ...lines, '', 'Find out why, fix it and push.'].join(
		'\n',
	);
}

/** A merge started from this session: running, or finished. */
export interface PullMergeRun {
	phase: 'merging' | 'merged';
	method: AgentPullMergeMethod;
	deleteBranch: boolean;
	/** How many commits it takes in, when the page knows. */
	commits: number | null;
	/** When it finished (when it started, while running). */
	at: string;
}

/** A merge can answer at once; the loader stays for this long so it doesn't just flash. */
export const MERGE_LOADER_MIN_MS = 1500;

class AgentPullsStore {
	/** Whether the person's GitHub is connected, from the last answer; null before the first. */
	github: AgentPullLookup['github'] | null = null;
	/** A pull request whose Merge confirm should open when its page shows it (the table's quick Merge). */
	mergeIntent: string | null = null;
	readonly pulls = observable.map<string, AgentPullStatus>();
	/** Pull requests the last read could not get (no access, gone, or the request failed). */
	readonly unreadable = observable.set<string>();
	/** When each pull request was last asked for (so several views don't ask at once). */
	private readonly askedAt = new Map<string, number>();

	/** Each repository's open pull requests for the `@` picker, by owner/name in lower case. */
	readonly open = observable.map<string, AgentOpenPulls>();
	private readonly openAskedAt = new Map<string, number>();

	/** Reads a repository's open pull requests (the picker opens it), at most once a minute. */
	async loadOpen(repository: string, minAgeMs = 60_000): Promise<void> {
		const key = repository.toLowerCase();
		const now = Date.now();
		if (now - (this.openAskedAt.get(key) ?? 0) < minAgeMs) return;
		this.openAskedAt.set(key, now);
		try {
			const response = await http.get<AgentOpenPulls>(Endpoints.KONVO_AGENTS_PULLS_OPEN, {
				query: {repository},
				mode: 'silent',
			});
			if (!response.ok) return;
			runInAction(() => {
				this.github = response.body.github;
				this.open.set(key, response.body);
			});
		} catch {
			// The picker still offers the chats' own pull requests; the next opening asks again.
			this.openAskedAt.delete(key);
		}
	}

	/** Each pull request's conversation on GitHub (the PR panel), by pullKey. */
	readonly conversations = observable.map<string, AgentPullConversation>();

	/** Reads the conversation (the API answers polls within a few seconds from memory). */
	async loadConversation(ref: AgentPullRef): Promise<void> {
		const response = await http.post<AgentPullConversation>(Endpoints.KONVO_AGENTS_PULLS_CONVERSATION, {
			body: {repository: ref.repository, number: ref.number},
		});
		runInAction(() => this.conversations.set(pullKey(ref), response.body));
	}

	/** Each pull request's page beyond the card (description, files, commits, activity), by pullKey. */
	readonly details = observable.map<string, AgentPullDetails>();

	/** Reads the page's details (the API answers polls within 15 s from memory). */
	async loadDetails(ref: AgentPullRef): Promise<void> {
		const response = await http.post<AgentPullDetails>(Endpoints.KONVO_AGENTS_PULLS_DETAILS, {
			body: {repository: ref.repository, number: ref.number},
		});
		runInAction(() => this.details.set(pullKey(ref), response.body));
	}

	/** Does it on GitHub as the person, then reads the pull request and its conversation again. */
	async act(
		ref: AgentPullRef,
		action: AgentPullAction,
		extra: {threadId?: string; body?: string} = {},
	): Promise<AgentPullActionResult> {
		const response = await http.post<AgentPullActionResult>(Endpoints.KONVO_AGENTS_PULLS_ACT, {
			body: {repository: ref.repository, number: ref.number, action, ...extra},
		});
		void this.loadConversation(ref).catch(() => undefined);
		if (this.details.has(pullKey(ref))) void this.loadDetails(ref).catch(() => undefined);
		void this.load([{repository: ref.repository, number: ref.number}], 0);
		return response.body;
	}

	/** Konvo's reviews, per pull request and head commit. */
	readonly reviews = observable.map<string, AgentPullReview>();
	/** Pull requests people asked this person to merge; null until loaded. */
	asks: Array<AgentMergeAsk> | null = null;
	/** Staff: who may merge from Konvo; null until loaded. */
	rights: AgentMergeRights | null = null;
	/** The pull request picked in a chat's panel (machineId/threadId → pullKey and when), this session. */
	readonly picked = observable.map<string, {key: string; at: number}>();

	/**
	 * The chat's pull request the person picked in its panel, while the chat still links it and
	 * links nothing newer: a pull request linked after the pick takes the card over again.
	 */
	pickedFor(
		threadKey: string,
		thread: Pick<AgentThreadSummary, 'pullRequests'> | null | undefined,
	): AgentPullRequest | null {
		const picked = this.picked.get(threadKey);
		if (!picked) return null;
		const pulls = thread?.pullRequests ?? [];
		if (pulls.some((pull) => linkedMs(pull) > picked.at)) return null;
		return pulls.find((pull) => pullKey(pull) === picked.key) ?? null;
	}

	pick(threadKey: string, ref: AgentPullRef): void {
		this.picked.set(threadKey, {key: pullKey(ref), at: Date.now()});
	}

	/** Cards the person folded or opened by hand (this session); otherwise the card decides. */
	readonly collapsed = observable.map<string, boolean>();

	constructor() {
		makeAutoObservable<this, 'askedAt'>(this, {askedAt: false}, {autoBind: true});
	}

	/** Opens the pull request's page with its Merge confirm showing. */
	openToMerge(ref: AgentPullRef): void {
		this.mergeIntent = pullKey(ref);
	}

	/** The page took the intent (or it was for another pull request). */
	clearMergeIntent(): void {
		this.mergeIntent = null;
	}

	resetForTesting(): void {
		this.github = null;
		this.pulls.clear();
		this.unreadable.clear();
		this.askedAt.clear();
		this.reviews.clear();
		this.details.clear();
		this.picked.clear();
		this.conversations.clear();
		this.mergeRuns.clear();
		this.asks = null;
		this.rights = null;
		this.collapsed.clear();
	}

	collapsedOf(ref: AgentPullRef): boolean | null {
		return this.collapsed.get(pullKey(ref)) ?? null;
	}

	setCollapsed(ref: AgentPullRef, value: boolean): void {
		this.collapsed.set(pullKey(ref), value);
	}

	review(ref: AgentPullRef, headSha: string): AgentPullReview | null {
		return this.reviews.get(`${pullKey(ref)}@${headSha}`) ?? null;
	}

	/** Konvo's review at the head commit (starts one when there is none). */
	async loadReview(ref: AgentPullRef, headSha: string): Promise<void> {
		try {
			const response = await http.post<AgentPullReview>(Endpoints.KONVO_AGENTS_PULLS_REVIEW, {
				body: {repository: ref.repository, number: ref.number},
				mode: 'silent',
			});
			if (!response.ok) return;
			runInAction(() => this.reviews.set(`${pullKey(ref)}@${response.body.headSha || headSha}`, response.body));
		} catch {
			// The next poll tries again.
		}
	}

	/** Merges started this session, by pullKey: kept in here so leaving the page and coming back still shows them. */
	readonly mergeRuns = observable.map<string, PullMergeRun>();

	mergeRun(ref: AgentPullRef): PullMergeRun | null {
		return this.mergeRuns.get(pullKey(ref)) ?? null;
	}

	/** Merges as the person (squash unless told); afterwards the card follows the release. Throws the API error. */
	async merge(
		ref: AgentPullRef,
		headSha: string,
		deleteBranch: boolean,
		release: Array<string>,
		method?: AgentPullMergeMethod,
		commits?: number,
	): Promise<AgentPullMergeResult> {
		const key = pullKey(ref);
		const started = Date.now();
		const run: PullMergeRun = {
			phase: 'merging',
			method: method ?? 'squash',
			deleteBranch,
			commits: commits ?? null,
			at: new Date(started).toISOString(),
		};
		this.mergeRuns.set(key, run);
		try {
			const response = await http.post<AgentPullMergeResult>(Endpoints.KONVO_AGENTS_PULLS_MERGE, {
				body: {
					repository: ref.repository,
					number: ref.number,
					headSha,
					deleteBranch,
					release,
					...(method && method !== 'squash' ? {method} : {}),
				},
			});
			const left = MERGE_LOADER_MIN_MS - (Date.now() - started);
			if (left > 0) await new Promise((resolve) => setTimeout(resolve, left));
			await this.load([ref], 0);
			runInAction(() => this.mergeRuns.set(key, {...run, phase: 'merged', at: new Date().toISOString()}));
			return response.body;
		} catch (error) {
			runInAction(() => this.mergeRuns.delete(key));
			throw error;
		}
	}

	/** Asks the people who may merge here; returns who was asked. Throws the API error. */
	async ask(ref: AgentPullRef): Promise<AgentPullAsked> {
		const response = await http.post<AgentPullAsked>(Endpoints.KONVO_AGENTS_PULLS_ASK, {
			body: {repository: ref.repository, number: ref.number},
		});
		runInAction(() => {
			const pull = this.pulls.get(pullKey(ref));
			if (pull) this.pulls.set(pullKey(ref), {...pull, asked: response.body});
		});
		return response.body;
	}

	async loadAsks(): Promise<void> {
		try {
			const response = await http.get<{asks: Array<AgentMergeAsk>}>(Endpoints.KONVO_AGENTS_MERGE_ASKS, {
				mode: 'silent',
			});
			if (!response.ok) return;
			runInAction(() => {
				this.asks = response.body.asks;
			});
		} catch {
			// Kept as it was.
		}
	}

	async dismissAsk(ref: AgentPullRef): Promise<void> {
		runInAction(() => {
			this.asks = (this.asks ?? []).filter((ask) => pullKey(ask) !== pullKey(ref));
		});
		await http.post(Endpoints.KONVO_AGENTS_MERGE_ASKS_DISMISS, {
			body: {repository: ref.repository, number: ref.number},
			mode: 'silent',
		});
	}

	async loadRights(): Promise<void> {
		const response = await http.get<AgentMergeRights>(Endpoints.KONVO_AGENTS_MERGE_RIGHTS, {mode: 'silent'});
		// Silent replies come back on errors too: everyone but staff gets a 403 body, not rights.
		if (!response.ok) return;
		runInAction(() => {
			this.rights = response.body;
		});
	}

	async saveRights(next: AgentMergeRights): Promise<void> {
		const before = this.rights;
		this.rights = next;
		try {
			const response = await http.put<AgentMergeRights>(Endpoints.KONVO_AGENTS_MERGE_RIGHTS, {body: next});
			runInAction(() => {
				this.rights = response.body;
			});
		} catch (error) {
			runInAction(() => {
				this.rights = before;
			});
			throw error;
		}
	}

	/** Asked for but not read: GitHub did not return it, or the request failed. */
	isUnreadable(ref: AgentPullRef): boolean {
		return this.unreadable.has(pullKey(ref));
	}

	get(ref: AgentPullRef | null | undefined): AgentPullStatus | null {
		return ref ? (this.pulls.get(pullKey(ref)) ?? null) : null;
	}

	private markUnreadable(refs: ReadonlyArray<AgentPullRef>): void {
		for (const ref of refs) this.unreadable.add(pullKey(ref));
	}

	/** Reads them from GitHub (through the API), skipping ones asked for in the last `minAgeMs`. */
	async load(refs: ReadonlyArray<AgentPullRef>, minAgeMs = 4_000): Promise<void> {
		const now = Date.now();
		const wanted = refs.filter((ref, index) => {
			const key = pullKey(ref);
			if (refs.findIndex((other) => pullKey(other) === key) !== index) return false;
			return now - (this.askedAt.get(key) ?? 0) >= minAgeMs;
		});
		if (!wanted.length) return;
		for (const ref of wanted) this.askedAt.set(pullKey(ref), now);
		for (let start = 0; start < wanted.length; start += AGENT_PULL_LOOKUP_MAX) {
			const pulls = wanted
				.slice(start, start + AGENT_PULL_LOOKUP_MAX)
				.map((ref) => ({repository: ref.repository, number: ref.number}));
			try {
				const response = await http.post<AgentPullLookup>(Endpoints.KONVO_AGENTS_PULLS_LOOKUP, {
					body: {pulls},
					mode: 'silent',
				});
				if (!response.ok) {
					this.markUnreadable(pulls);
					continue;
				}
				runInAction(() => {
					this.github = response.body.github;
					for (const ref of pulls) this.unreadable.add(pullKey(ref));
					for (const pull of response.body.pulls) {
						this.pulls.set(pullKey(pull), pull);
						this.unreadable.delete(pullKey(pull));
					}
				});
			} catch {
				// Kept as it was; the next poll tries again.
				this.markUnreadable(pulls);
			}
		}
	}
}

const store = new AgentPullsStore();
export default store;
